import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Building2,
  Plus,
  Loader2,
  ArrowRight,
  Phone,
  Mail,
  User as UserIcon,
  CalendarClock,
  Clapperboard,
} from "lucide-react";
import { toast } from "sonner";
import { clientsApi } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ProjectsClientsSwitch from "@/components/ProjectsClientsSwitch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

/**
 * ClientsPage — top-level list of all clients with a quick create dialog.
 *
 * Layout: cards in a responsive grid. Each card shows
 *   - client name + contact + phone/email
 *   - summary chips: N active projects · N upcoming shoots · N due
 *   - Open → routes to /clients/:id
 *
 * The list endpoint returns the full client bundle including summary
 * counts, so this page needs a single request.
 */
export default function ClientsPage() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const list = await clientsApi.list();
      setClients(list);
    } catch {
      toast.error("Failed to load clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clients;
    return clients.filter((c) => {
      const haystack = [c.name, c.contact_name, c.email, c.phone]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [clients, query]);

  return (
    <div className="min-h-full bg-zinc-950">
      <section className="max-w-screen-2xl mx-auto px-6 lg:px-10 pt-8 pb-6">
        <div className="mb-6">
          <ProjectsClientsSwitch />
        </div>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl text-zinc-100">
              Clients
            </h1>
            <div className="text-overline text-zinc-500 mt-1">
              {clients.length} {clients.length === 1 ? "client" : "clients"}
            </div>
          </div>
          <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
            <div className="relative flex-1 sm:flex-none">
              <Input
                data-testid="client-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search clients…"
                className="w-full sm:w-64 bg-zinc-900 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
            {isAdmin && (
              <NewClientDialog open={open} setOpen={setOpen} onCreated={(c) => {
                setClients((prev) =>
                  [...prev, c].sort((a, b) =>
                    (a.name || "").toLowerCase().localeCompare((b.name || "").toLowerCase()),
                  ),
                );
              }} />
            )}
          </div>
        </div>
      </section>
      <section className="max-w-screen-2xl mx-auto px-6 lg:px-10 pb-24">
        {loading ? (
          <div className="flex items-center gap-2 text-zinc-500">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading clients…
          </div>
        ) : clients.length === 0 ? (
          <div
            data-testid="clients-empty-state"
            className="text-center py-24 text-zinc-500"
          >
            <Building2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
            <p className="font-heading text-xl text-zinc-300 mb-1">
              No clients yet
            </p>
            <p className="text-sm">
              Add a client from the button above or from the New Project dialog.
            </p>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-sm text-zinc-500 italic py-8">
            No clients match &ldquo;{query}&rdquo;.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((c, idx) => (
              <ClientCard key={c.id} client={c} idx={idx} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

// Client cover gradients — mirrors project card palette so a client
// visually inherits the accent of its work. Keys match Project.cover_color.
const CLIENT_COVER_GRADIENTS = {
  amber: "from-amber-500/30 via-amber-700/10 to-zinc-900",
  rose: "from-rose-500/30 via-rose-700/10 to-zinc-900",
  teal: "from-teal-500/30 via-teal-700/10 to-zinc-900",
  violet: "from-violet-500/30 via-violet-700/10 to-zinc-900",
  blue: "from-blue-500/30 via-blue-700/10 to-zinc-900",
};

// Dynamic gradient that tracks the active white-label brand accent
// via the CSS vars BrandProvider writes to <html>. Applied inline so a
// client cover can flip color when a tenant switches without adding
// per-brand Tailwind variants.
const CLIENT_BRAND_COVER_STYLE = {
  backgroundImage:
    "linear-gradient(135deg, var(--brand-accent-soft) 0%, var(--brand-accent-soft) 30%, rgba(24,24,27,0.9) 75%, rgb(24 24 27) 100%)",
};

function ClientCard({ client, idx }) {
  const s = client.summary || {};
  const isBrandCover = client.cover_color === "brand";
  const gradient =
    CLIENT_COVER_GRADIENTS[client.cover_color] || "from-zinc-800/40 to-zinc-900";
  return (
    <Link
      to={`/clients/${client.id}`}
      data-testid={`client-card-${client.id}`}
      data-cover-color={client.cover_color || "none"}
      style={{ animationDelay: `${idx * 40}ms` }}
      className="animate-fade-up group relative rounded-md overflow-hidden border border-zinc-800 bg-zinc-900 hover:border-amber-500/40 transition-all flex flex-col"
    >
      <div
        className={isBrandCover ? "h-14" : `h-14 bg-gradient-to-br ${gradient}`}
        style={isBrandCover ? CLIENT_BRAND_COVER_STYLE : undefined}
      />
      <div className="p-5 flex flex-col gap-3">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-overline text-amber-500/80">
            <Clapperboard className="w-3 h-3" /> Client
          </div>
          <h3 className="font-heading text-xl text-zinc-100 truncate group-hover:text-amber-500 transition-colors">
            {client.name}
          </h3>
        </div>
        <ArrowRight className="w-4 h-4 text-zinc-600 group-hover:text-amber-500 transition-colors mt-1 shrink-0" />
      </div>
      <div className="text-xs text-zinc-500 flex flex-wrap items-center gap-x-3 gap-y-1 min-h-[1.25rem]">
        {client.contact_name && (
          <span className="flex items-center gap-1">
            <UserIcon className="w-3 h-3" /> {client.contact_name}
          </span>
        )}
        {client.phone && (
          <span className="flex items-center gap-1">
            <Phone className="w-3 h-3" /> {client.phone}
          </span>
        )}
        {client.email && (
          <span className="flex items-center gap-1 truncate max-w-[200px]">
            <Mail className="w-3 h-3" /> {client.email}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap pt-1">
        <SummaryChip
          testId={`client-active-count-${client.id}`}
          label={`${s.active_project_count || 0} active`}
          tone="emerald"
        />
        {s.upcoming_shoots > 0 && (
          <SummaryChip
            testId={`client-upcoming-shoots-${client.id}`}
            label={`${s.upcoming_shoots} shoot${s.upcoming_shoots === 1 ? "" : "s"}`}
            tone="amber"
            hint={s.next_shoot_date ? `Next: ${s.next_shoot_date}` : null}
          />
        )}
        {s.upcoming_tasks_due > 0 && (
          <SummaryChip
            testId={`client-upcoming-tasks-${client.id}`}
            label={`${s.upcoming_tasks_due} due`}
            tone="rose"
            hint={s.next_task_due_date ? `Next: ${s.next_task_due_date}` : null}
          />
        )}
      </div>
      {(s.next_shoot_date || s.next_task_due_date) && (
        <div className="text-[11px] font-mono text-zinc-600 flex items-center gap-1">
          <CalendarClock className="w-3 h-3" />
          {s.next_shoot_date && <span>Shoot {s.next_shoot_date}</span>}
          {s.next_shoot_date && s.next_task_due_date && (
            <span className="text-zinc-700">·</span>
          )}
          {s.next_task_due_date && <span>Task {s.next_task_due_date}</span>}
        </div>
      )}
      </div>
    </Link>
  );
}

const CHIP_TONE = {
  emerald: "border-emerald-500/40 text-emerald-300 bg-emerald-500/10",
  amber: "border-amber-500/40 text-amber-300 bg-amber-500/10",
  rose: "border-rose-500/40 text-rose-300 bg-rose-500/10",
};

function SummaryChip({ label, tone, hint, testId }) {
  return (
    <span
      data-testid={testId}
      title={hint || undefined}
      className={`inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-mono font-bold uppercase tracking-wider border ${CHIP_TONE[tone]}`}
    >
      {label}
    </span>
  );
}

/**
 * Compact color picker for the client accent gradient. Same 5 palette
 * keys as the project card. Empty string = auto-inherit from newest
 * project.
 */
export function ClientCoverPicker({ value, onChange }) {
  // Inline hex on named swatches keeps the picker faithful under any
  // brand — the amber → brand-accent remap in index.css would otherwise
  // repaint the "amber" swatch to match the active brand accent, making
  // it indistinguishable from "Follow brand".
  const swatches = [
    { key: "",       label: "Auto",         swatch: "bg-gradient-to-br from-zinc-700 to-zinc-900" },
    { key: "amber",  label: "Amber",        swatch: "",  inlineStyle: { background: "linear-gradient(to bottom right, #f59e0b, #92400e)" } },
    { key: "rose",   label: "Rose",         swatch: "",  inlineStyle: { background: "linear-gradient(to bottom right, #f43f5e, #9f1239)" } },
    { key: "teal",   label: "Teal",         swatch: "",  inlineStyle: { background: "linear-gradient(to bottom right, #14b8a6, #115e59)" } },
    { key: "violet", label: "Violet",       swatch: "",  inlineStyle: { background: "linear-gradient(to bottom right, #8b5cf6, #5b21b6)" } },
    { key: "blue",   label: "Blue",         swatch: "",  inlineStyle: { background: "linear-gradient(to bottom right, #3b82f6, #1e40af)" } },
    { key: "brand",  label: "Follow brand", swatch: "",  inlineStyle: { backgroundColor: "var(--brand-accent)" } },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="client-cover-picker">
      {swatches.map((s) => {
        const isActive = (value || "") === s.key;
        return (
          <button
            key={s.key || "auto"}
            type="button"
            onClick={() => onChange(s.key)}
            data-testid={`client-cover-${s.key || "auto"}`}
            data-active={isActive ? "true" : "false"}
            title={s.label}
            style={s.inlineStyle}
            className={`w-8 h-8 rounded-md ${s.swatch} border-2 transition-all ${
              isActive ? "border-amber-400 scale-110" : "border-zinc-800 hover:border-zinc-600"
            }`}
          >
            {s.key === "" && (
              <span className="text-[8px] text-zinc-300 font-mono uppercase">Auto</span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/**
 * NewClientDialog — admin-only.
 * Same fields as the backend Client model. Used from both ClientsPage
 * and the ClientDetailPage's "Edit client" flow (via prop).
 */
function NewClientDialog({ open, setOpen, onCreated }) {
  const [form, setForm] = useState({
    name: "",
    contact_name: "",
    address: "",
    phone: "",
    email: "",
    cover_color: "",
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    try {
      const created = await clientsApi.create(form);
      // Keep sidebar + any other mounted client list in sync without
      // requiring a page refresh.
      window.dispatchEvent(new CustomEvent("clients:changed"));
      toast.success(`Client "${created.name}" created`);
      onCreated?.(created);
      setOpen(false);
      setForm({ name: "", contact_name: "", address: "", phone: "", email: "", cover_color: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create client");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="new-client-btn"
          className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
        >
          <Plus className="w-4 h-4 mr-1" /> New Client
        </Button>
      </DialogTrigger>
      <DialogContent
        data-testid="new-client-dialog"
        className="bg-zinc-950 border-zinc-800 text-zinc-100 max-w-xl"
      >
        <DialogHeader>
          <DialogTitle>New Client</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-2">
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">
              Client Name <span className="text-rose-400">*</span>
            </Label>
            <Input
              data-testid="new-client-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Stream Realty, Apple, A24…"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Contact Name</Label>
              <Input
                data-testid="new-client-contact"
                value={form.contact_name}
                onChange={(e) => setForm({ ...form, contact_name: e.target.value })}
                placeholder="Jane Doe"
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-overline text-zinc-500">Phone</Label>
              <Input
                data-testid="new-client-phone"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                placeholder="(555) 123-4567"
                className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Email</Label>
            <Input
              data-testid="new-client-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="ops@stream.com"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">Address</Label>
            <Input
              data-testid="new-client-address"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              placeholder="123 Main St, Dallas TX"
              className="bg-zinc-950 border-zinc-800 focus-visible:ring-amber-500"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-overline text-zinc-500">
              Accent Color <span className="text-zinc-600 normal-case">(auto = inherit from newest project)</span>
            </Label>
            <ClientCoverPicker
              value={form.cover_color}
              onChange={(k) => setForm({ ...form, cover_color: k })}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
              className="bg-transparent border-zinc-800 text-zinc-300 hover:bg-zinc-900"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving}
              data-testid="new-client-submit"
              className="bg-amber-500 text-zinc-950 hover:bg-amber-400"
            >
              {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
              Create Client
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
